import { useEffect, useMemo, useState } from "react";
import { Building2Icon, CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@component/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@component/command";
import { Button } from "@component/button";
import { cn } from "@util/tailwind";
import { getListWorkspaceCompany } from "@service/employer";
import { WorkSpaceCompanyItem } from "@type/company";
import { AdminWorkspaceCompanyOption } from "@type/admin";
import { catchErrorLog } from "@util/sentry";

interface Props {
    value: string;
    onChange: (companyId: string) => void;
    className?: string;
}

const ALL_COMPANIES_OPTION: AdminWorkspaceCompanyOption = {
    id: "",
    name: "All companies",
};

export default function CompanyFilter({ value, onChange, className }: Props) {
    const [open, setOpen] = useState(false);
    const [companies, setCompanies] = useState<AdminWorkspaceCompanyOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        getListWorkspaceCompany({ page: 1, limit: 500 })
            .then((resp) => {
                if (cancelled) return;
                const list = (resp?.data?.data ?? []) as WorkSpaceCompanyItem[];
                const mapped = list
                    .filter((c) => c.company_id && c.company_name)
                    .map<AdminWorkspaceCompanyOption>((c) => ({
                        id: c.company_id,
                        name: c.company_name,
                    }));
                setCompanies(mapped);
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Hiring Activity: failed to load workspace companies: ${err}`);
                setCompanies([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const options = useMemo<AdminWorkspaceCompanyOption[]>(
        () => [ALL_COMPANIES_OPTION, ...companies],
        [companies]
    );

    const selected = options.find((o) => o.id === value) ?? ALL_COMPANIES_OPTION;

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        "w-full sm:w-[280px] justify-between font-normal",
                        className
                    )}
                >
                    <span className="flex items-center gap-2 truncate">
                        <Building2Icon className="w-4 h-4 text-neutral-500 flex-shrink-0" />
                        <span className="truncate">{selected.name}</span>
                    </span>
                    <ChevronsUpDownIcon className="w-4 h-4 opacity-50 flex-shrink-0 ml-2" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="start">
                <Command>
                    <CommandInput placeholder="Search company…" />
                    <CommandList>
                        <CommandEmpty>
                            {isLoading ? "Loading companies…" : "No company found."}
                        </CommandEmpty>
                        <CommandGroup>
                            {options.map((option) => (
                                <CommandItem
                                    key={option.id || "__all__"}
                                    value={`${option.name} ${option.id}`}
                                    onSelect={() => {
                                        onChange(option.id);
                                        setOpen(false);
                                    }}
                                    className="cursor-pointer"
                                >
                                    <CheckIcon
                                        className={cn(
                                            "mr-2 h-4 w-4",
                                            value === option.id ? "opacity-100" : "opacity-0"
                                        )}
                                    />
                                    <span className="truncate">{option.name}</span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
